<template>
    <ModalBase
        :name="'descuento-plazo-' + plazoId"
        title="Aplicar Descuento"
        @close="handleClose"
    >
        <div class="tw:space-y-4">
            <p class="tw:text-sm tw:text-gray-700">
                ¿Qué tipo de descuento deseas aplicar?
            </p>

            <div class="tw:flex tw:gap-2">
                <Button
                    :variant="discountMode === 'percentage' ? 'primary' : 'border'"
                    class="tw:flex-1"
                    @click="setMode('percentage')"
                >
                    Descuento %
                </Button>
                <Button
                    :variant="discountMode === 'fixed' ? 'primary' : 'border'"
                    class="tw:flex-1"
                    @click="setMode('fixed')"
                >
                    Descuento €
                </Button>
            </div>

            <div v-if="discountMode === 'percentage'" class="tw:space-y-2">
                <BaseMultiselect
                    :value="discountPercentage"
                    :options="percentageOptions"
                    label="Porcentaje de descuento"
                    labelKey="name"
                    valueKey="id"
                    @input="discountPercentage = $event"
                />
            </div>

            <div v-if="discountMode === 'fixed'">
                <BaseInputNumber
                    :value="discountFixed"
                    @input="discountFixed = $event"
                    label="Monto del descuento"
                    :min="0"
                    :step="0.01"
                >
                    <template #suffix>
                        <span class="tw:text-gray-600 tw:font-medium">€</span>
                    </template>
                </BaseInputNumber>
            </div>

            <div class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:text-xs tw:text-gray-600">
                <p class="tw:mb-1">
                    <strong>Plazo Nº{{ plazoId }}</strong>
                </p>
                <p>
                    Importe actual: <strong>{{ importe | formatear_precio }}</strong>
                </p>
            </div>
        </div>

        <template #footer>
            <div class="tw:flex tw:gap-2 tw:justify-end">
                <Button variant="border" @click="handleClose">
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    :disabled="!hasValidDiscount"
                    @click="handleConfirm"
                >
                    Aplicar
                </Button>
            </div>
        </template>
    </ModalBase>
</template>

<script setup>
    import { ref, computed, getCurrentInstance } from 'vue'
    import ModalBase from '@/modules/admin/ui/ModalBase.vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue'
    import BaseInputNumber from '@/modules/admin/ui/BaseInputNumber.vue'

    const props = defineProps({
        importe: {
            type: Number,
            required: true
        },
        plazoId: {
            type: [Number, String],
            required: true
        }
    })

    const emit = defineEmits(['descontar-plazo', 'close'])

    const percentageOptions = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50].map(n => ({ id: n, name: n + '%' }))

    const instance = getCurrentInstance()
    const discountMode = ref('percentage')
    const discountPercentage = ref(null)
    const discountFixed = ref(null)

    const setMode = mode => {
        discountMode.value = mode
        if (mode === 'percentage') {
            discountFixed.value = null
        } else {
            discountPercentage.value = null
        }
    }

    const hasValidDiscount = computed(() => {
        if (discountMode.value === 'percentage') {
            return discountPercentage.value !== null && discountPercentage.value > 0
        } else {
            return discountFixed.value !== null && discountFixed.value > 0
        }
    })

    const handleConfirm = () => {
        if (!hasValidDiscount.value) return

        const descuento_integro = discountMode.value === 'fixed' ? discountFixed.value : 0
        const descuento_porcentaje = discountMode.value === 'percentage' ? discountPercentage.value : 0

        emit('descontar-plazo', {
            descuento_integro,
            descuento_porcentaje,
            plazo_id: props.plazoId
        })
        handleClose()
    }

    const handleClose = () => {
        discountMode.value = 'percentage'
        discountPercentage.value = null
        discountFixed.value = null
        instance.proxy.$modal.hide('descuento-plazo-' + props.plazoId)
        emit('close')
    }
</script>
